import { getFitnessWorkoutExperienceCopy } from '@/lib/fitnessWorkoutExperienceCopy';
import { getFitnessGuidedCopy } from '@/lib/fitnessGuidedCopy';
import { useLanguage } from '@/i18n/LanguageProvider';
import { localizeFitnessProductExerciseName } from '@/lib/fitnessProductExerciseLabels';
import { readPerformedWorkoutExercises } from '@/lib/fitness/workoutHistory';
import { workoutHistoryProposal, type FreeWorkoutItem } from '@/lib/fitness/freeWorkoutBuilder';

export default function WorkoutHistoryDetail({ exercises, onRepeat }: { exercises: unknown; onRepeat?: (items: FreeWorkoutItem[]) => void }) {
  const { t, lang } = useLanguage();
  const copy = getFitnessWorkoutExperienceCopy(lang), guided = getFitnessGuidedCopy(lang);
  const performed = readPerformedWorkoutExercises(exercises);
  if (!performed.length) return null;
  return <details className="mt-2 min-w-0">
    <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{t('fitness.builder.details')}</summary>
    <div className="space-y-4 pb-2">
      {performed.map((exercise, index) => <div key={index}>
        <p className="mb-2 text-sm font-semibold">{localizeFitnessProductExerciseName(exercise.name, lang) ?? (exercise.name.startsWith('fitness.') ? t(exercise.name) : exercise.name)}</p>
        {exercise.groupId && <p className="mb-2 text-xs text-primary">{exercise.groupType === 'circuit' ? guided.circuit : guided.superset}</p>}
        {exercise.notes && <p className="ph-no-capture mb-3 whitespace-pre-wrap break-words text-sm text-muted-foreground" data-private="true">{exercise.notes}</p>}
        <div className="overflow-x-auto"><table className="w-full text-left text-sm tabular-nums">
          <thead className="text-xs text-muted-foreground"><tr>
            <th scope="col" className="py-2">{t('fitness.workout.sets')}</th>
            <th scope="col" className="py-2">{t('fitness.workout.weightLabel')}</th>
            <th scope="col" className="py-2">{t(exercise.timed ? 'fitness.ux.seconds' : 'fitness.workout.reps')}</th>
            <th scope="col" className="py-2 pl-3">{copy.setType}</th>
            <th scope="col" className="py-2 pl-3">{copy.rir}</th>
          </tr></thead>
          <tbody>{exercise.sets.map((set, setIndex) => <tr key={setIndex} className="border-t border-border/50">
            <td className="py-2">{setIndex + 1}</td><td className="py-2">{set.weight} {t('fitness.weightUnit')}</td><td className="py-2">{set.reps}</td>
            <td className="py-2 pl-3">{set.type === 'warmup' ? copy.warmup : copy.working}</td>
            <td className="py-2 pl-3">{set.rir ?? '—'}</td>
          </tr>)}</tbody>
        </table></div>
      </div>)}
      {onRepeat && <button type="button" className="min-h-11 w-full rounded-xl border border-primary/40 px-3 py-2 text-sm font-semibold text-primary"
        onClick={() => { const proposal = workoutHistoryProposal(exercises); if (proposal.length) onRepeat(proposal); }}>{t('fitness.builder.repeat')}</button>}
    </div>
  </details>;
}
